type ExternalLinkClickEvent = {
  preventDefault(): void;
  defaultPrevented: boolean;
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
};

/**
 * Prefer handing off external navigation to Android's intent resolver (Chrome / default browser).
 * Desktop and iOS keep normal https navigation via {@link resolveExternalNavigationDestination}.
 */
export function shouldNavigateExternalViaAndroidIntent(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  return /\bAndroid\b/i.test(navigator.userAgent);
}

export function resolveExternalNavigationDestination(httpsUrl: string): string {
  const t = httpsUrl.trim();
  if (!/^https?:\/\//i.test(t)) {
    return t;
  }
  return shouldNavigateExternalViaAndroidIntent()
    ? httpsUrlToAndroidBrowserIntentUrl(t)
    : t;
}

/**
 * Use with `href={originalHttpsUrl}` so PC browsers follow the normal URL; on Android only,
 * intercept primary clicks and navigate via intent (WebView START_INTENT flow).
 */
export function maybeInterceptExternalLinkForAndroidIntent(
  e: ExternalLinkClickEvent,
  originalHttpsUrl: string,
): void {
  if (e.defaultPrevented || e.button !== 0) {
    return;
  }
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
    return;
  }
  const t = originalHttpsUrl.trim();
  if (!/^https?:\/\//i.test(t)) {
    return;
  }
  if (shouldNavigateExternalViaAndroidIntent()) {
    e.preventDefault();
    window.location.assign(httpsUrlToAndroidBrowserIntentUrl(t));
  }
}

/**
 * Builds an Android Intent URI so taps from an in-app WebView can delegate to the
 * system browser (handled as {@link WebLinkType.StartIntent} in moment-client).
 *
 * @see https://developer.chrome.com/docs/multidevice/android/intents
 */
export function httpsUrlToAndroidBrowserIntentUrl(url: string): string {
  const t = url.trim();
  if (!/^https?:\/\//i.test(t) || /^intent:/i.test(t)) {
    return t;
  }
  try {
    const fallback = encodeURIComponent(t);
    const u = new URL(t);
    const scheme = u.protocol.replace(':', '');
    const hashInPath = u.hash ? u.hash.replace('#', '%23') : '';
    const pathPart = `${u.pathname}${u.search}${hashInPath}`;
    const hostPort = u.host;
    return `intent://${hostPort}${pathPart}#Intent;scheme=${scheme};action=android.intent.action.VIEW;category=android.intent.category.BROWSABLE;S.browser_fallback_url=${fallback};end`;
  } catch {
    return t;
  }
}
